import { Injectable, signal, computed, inject } from '@angular/core';
import {
  Campaign,
  CampaignStatus,
  CampaignFilters,
  CsvValidationResult,
  CampaignProgress,
  CreateCampaignDto,
  MarkPaidDto,
  PackageType,
  PackageConfig,
  PACKAGE_TYPE_METADATA,
  formatEurCents,
  formatAddressRange,
} from '@features/campaigns/models/campaign.model';
import { CampaignsService } from '@features/campaigns/services/campaigns.service';

export interface LoadingState {
  isLoading: boolean;
  error: string | null;
}

export interface CampaignStoreState {
  currentCampaign: Campaign | null;
  campaigns: Campaign[];
  csvValidation: CsvValidationResult | null;
  progress: CampaignProgress | null;
  isUploading: boolean;
  filters: CampaignFilters;
  packageConfig: Record<PackageType, PackageConfig> | null;
  loading: LoadingState;
}

@Injectable({
  providedIn: 'root',
})
export class CampaignStore {
  private readonly campaignsService = inject(CampaignsService);

  // Private signal for state management
  private readonly _state = signal<CampaignStoreState>({
    currentCampaign: null,
    campaigns: [],
    csvValidation: null,
    progress: null,
    isUploading: false,
    filters: { includeArchived: false },
    packageConfig: null,
    loading: { isLoading: false, error: null },
  });

  // Public computed signals for reactive access
  readonly currentCampaign = computed(() => this._state().currentCampaign);
  readonly campaigns = computed(() => this._state().campaigns);
  readonly csvValidation = computed(() => this._state().csvValidation);
  readonly progress = computed(() => this._state().progress);
  readonly isUploading = computed(() => this._state().isUploading);
  readonly loading = computed(() => this._state().loading);
  readonly isLoading = computed(() => this._state().loading.isLoading);
  readonly hasError = computed(() => !!this._state().loading.error);
  readonly errorMessage = computed(() => this._state().loading.error);
  readonly packageConfig = computed(() => this._state().packageConfig);

  /**
   * Dynamic package options derived from API config, with fallback to static metadata
   */
  readonly packageOptions = computed(() => {
    const config = this._state().packageConfig;
    if (config) {
      return Object.entries(config).map(([key, cfg]) => ({
        value: key as PackageType,
        label: cfg.label,
        description: cfg.description,
        addressRange: formatAddressRange(cfg),
        priceRange: formatEurCents(cfg.priceEurCents),
        min: cfg.min,
        max: cfg.max,
        priceEurCents: cfg.priceEurCents,
      }));
    }
    // Fallback to static metadata
    return Object.entries(PACKAGE_TYPE_METADATA).map(([key, meta]) => ({
      value: key as PackageType,
      label: meta.label,
      description: meta.description,
      addressRange: meta.addressRange,
      priceRange: meta.priceRange,
      min: 0,
      max: null as number | null,
      priceEurCents: 0,
    }));
  });

  // Current campaign computed properties
  readonly campaignId = computed(
    () => this._state().currentCampaign?.id || null,
  );
  readonly organizationId = computed(
    () => this._state().currentCampaign?.organizationId || null,
  );

  // Filtered views
  readonly activeCampaigns = computed(() =>
    this._state().campaigns.filter(
      (c) =>
        c.status !== CampaignStatus.ARCHIVED &&
        c.status !== CampaignStatus.CANCELLED,
    ),
  );
  readonly processingCampaigns = computed(() =>
    this._state().campaigns.filter(
      (c) => c.status === CampaignStatus.PROCESSING,
    ),
  );

  /**
   * Load a campaign by ID and set it as current campaign
   */
  async loadCampaign(
    organizationId: string,
    campaignId: string,
  ): Promise<void> {
    // Single loading state update
    this._state.update((state) => ({
      ...state,
      loading: { isLoading: true, error: null },
    }));

    try {
      console.log('[CampaignStore] Loading campaign:', campaignId);
      const campaign = await this.campaignsService.getCampaignById(
        organizationId,
        campaignId,
      );

      // Single atomic update with result
      this._state.update((state) => ({
        ...state,
        currentCampaign: campaign,
        loading: { isLoading: false, error: null },
      }));

      console.log('[CampaignStore] Campaign loaded successfully:', campaign);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Die Kampagne konnte nicht geladen werden.';
      console.error('[CampaignStore] Error loading campaign:', error);

      // Single update for error state
      this._state.update((state) => ({
        ...state,
        currentCampaign: null,
        loading: { isLoading: false, error: errorMessage },
      }));
    }
  }

  /**
   * Load campaigns for the current organization
   */
  async loadCampaignsByOrganization(organizationId: string): Promise<void> {
    this.setLoading(true, null);

    try {
      console.log(
        '[CampaignStore] Loading campaigns for organization:',
        organizationId,
      );
      const campaigns =
        await this.campaignsService.getCampaigns(organizationId);

      this._state.update((state) => ({
        ...state,
        campaigns,
        loading: { isLoading: false, error: null },
      }));

      console.log(
        '[CampaignStore] Campaigns loaded successfully:',
        campaigns.length,
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load campaigns';
      console.error('[CampaignStore] Error loading campaigns:', error);

      this.setLoading(false, errorMessage);
    }
  }

  /**
   * Update campaign status
   */
  async updateCampaignStatus(
    organizationId: string,
    campaignId: string,
    status: any,
  ): Promise<void> {
    try {
      console.log(
        '[CampaignStore] Updating campaign status:',
        campaignId,
        status,
      );
      const updatedCampaign = await this.campaignsService.updateCampaign(
        organizationId,
        campaignId,
        { status },
      );

      // Update the current campaign if it matches
      this._state.update((state) => ({
        ...state,
        currentCampaign:
          state.currentCampaign?.id === campaignId
            ? updatedCampaign
            : state.currentCampaign,
        campaigns: state.campaigns.map((c) =>
          c.id === campaignId ? updatedCampaign : c,
        ),
      }));

      console.log('[CampaignStore] Campaign status updated successfully');
    } catch (error) {
      console.error('[CampaignStore] Error updating campaign status:', error);
      throw error;
    }
  }

  /**
   * Clear current campaign
   */
  clearCurrentCampaign(): void {
    this._state.update((state) => ({
      ...state,
      currentCampaign: null,
      loading: { isLoading: false, error: null },
    }));
  }

  /**
   * Clear all state
   */
  clear(): void {
    this._state.set({
      currentCampaign: null,
      campaigns: [],
      csvValidation: null,
      progress: null,
      isUploading: false,
      filters: { includeArchived: false },
      packageConfig: null,
      loading: { isLoading: false, error: null },
    });
  }

  // ─── Package Config ─────────────────────────────────────────────────────────

  /**
   * Load package configuration from the backend API
   */
  async loadPackageConfig(organizationId: string): Promise<void> {
    try {
      const config =
        await this.campaignsService.getPackageConfig(organizationId);
      this._state.update((state) => ({ ...state, packageConfig: config }));
    } catch (error) {
      console.warn(
        '[CampaignStore] Failed to load package config, using fallback:',
        error,
      );
    }
  }

  // ─── Campaign CRUD ──────────────────────────────────────────────────────────

  /**
   * Create a new campaign
   */
  async createCampaign(
    organizationId: string,
    dto: CreateCampaignDto,
  ): Promise<Campaign> {
    try {
      const campaign = await this.campaignsService.createCampaign(
        organizationId,
        dto,
      );
      this._state.update((state) => ({
        ...state,
        campaigns: [campaign, ...state.campaigns],
      }));
      return campaign;
    } catch (error) {
      console.error('[CampaignStore] Error creating campaign:', error);
      throw error;
    }
  }

  /**
   * Delete a campaign
   */
  async deleteCampaign(
    organizationId: string,
    campaignId: string,
  ): Promise<void> {
    try {
      await this.campaignsService.deleteCampaign(organizationId, campaignId);
      this._state.update((state) => ({
        ...state,
        campaigns: state.campaigns.filter((c) => c.id !== campaignId),
        currentCampaign:
          state.currentCampaign?.id === campaignId
            ? null
            : state.currentCampaign,
      }));
    } catch (error) {
      console.error('[CampaignStore] Error deleting campaign:', error);
      throw error;
    }
  }

  // ─── Business Logic ─────────────────────────────────────────────────────────

  /**
   * Mark a campaign as paid
   */
  async markPaid(
    organizationId: string,
    campaignId: string,
    dto: MarkPaidDto,
  ): Promise<void> {
    try {
      const updated = await this.campaignsService.markPaid(
        organizationId,
        campaignId,
        dto,
      );
      this.updateCampaignInState(campaignId, updated);
    } catch (error) {
      console.error('[CampaignStore] Error marking campaign as paid:', error);
      throw error;
    }
  }

  /** @deprecated Campaign analyses are now created at campaign creation time. */
  async uploadCsv(_organizationId: string, _campaignId: string, _file: File): Promise<void> {
    throw new Error('uploadCsv is no longer supported. Analyses are created during campaign creation.');
  }

  /** @deprecated Campaign analyses are now created at campaign creation time. */
  async startProcessing(_organizationId: string, _campaignId: string): Promise<{ analysesCreated: number }> {
    throw new Error('startProcessing is no longer supported. Analyses are created during campaign creation.');
  }

  /**
   * Poll progress for a processing campaign
   */
  async pollProgress(
    organizationId: string,
    campaignId: string,
  ): Promise<void> {
    try {
      const progress = await this.campaignsService.getProgress(
        organizationId,
        campaignId,
      );
      this._state.update((state) => ({
        ...state,
        progress,
        // Also update current campaign progress values if it matches
        currentCampaign:
          state.currentCampaign?.id === campaignId
            ? {
                ...state.currentCampaign,
                status: progress.status,
                completedAnalyses: progress.completedAnalyses,
                failedAnalyses: progress.failedAnalyses,
                processingAnalyses: progress.processingAnalyses,
                progress: progress.progress,
              }
            : state.currentCampaign,
      }));
    } catch (error) {
      console.error('[CampaignStore] Error polling progress:', error);
    }
  }

  /**
   * Archive a campaign
   */
  async archive(organizationId: string, campaignId: string): Promise<void> {
    try {
      const updated = await this.campaignsService.archiveCampaign(
        organizationId,
        campaignId,
      );
      this.updateCampaignInState(campaignId, updated);
    } catch (error) {
      console.error('[CampaignStore] Error archiving campaign:', error);
      throw error;
    }
  }

  /**
   * Clear CSV validation state
   */
  clearCsvValidation(): void {
    this._state.update((state) => ({
      ...state,
      csvValidation: null,
    }));
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  private updateCampaignInState(campaignId: string, updated: Campaign): void {
    this._state.update((state) => ({
      ...state,
      currentCampaign:
        state.currentCampaign?.id === campaignId
          ? updated
          : state.currentCampaign,
      campaigns: state.campaigns.map((c) =>
        c.id === campaignId ? updated : c,
      ),
    }));
  }

  /**
   * Set loading state
   */
  private setLoading(isLoading: boolean, error: string | null): void {
    this._state.update((state) => ({
      ...state,
      loading: { isLoading, error },
    }));
  }
}
